/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router'
import Page from '../../common/Page'
import $t from '../../../i18n'
import { useDispatch, useSelector } from 'react-redux'
import { validateDOB } from '../../../utils/validations'
import validateZipcode, {
  ZIP_CODE_NOT_FOUND
} from '../../../utils/validateZipcode'
import SelectedPlanInfo from '../../common/SelectedPlanInfo/SelectedPlanInfo'
import MemberDetailsCard from './MemberDetailsCard'
import { relationship } from '../../../consts'
import { Button } from 'antd'
import { isEmpty } from 'lodash'
import Actions from '../../../actions'
import { isValidPhoneNumber } from 'react-phone-number-input'
import { STEPS } from 'consts/flowConsts'
import { sortByRelation } from '../../../utils/memberUtils'
import routes from 'consts/routes'
import { scrollToError } from 'utils/generalUtils'
import useApplicationValidation from 'hooks/useApplicationValidation'

import './Details.css'

const getValidationErrors = (
  ageRequirments,
  {
    dob,
    sex,
    firstName,
    lastName,
    phone,
    email,
    prefferedLanguage,
    ssn,
    relation,
    homeAddress,
    mailingAddress,
    isMailingAddressAsHomeAddress,
    isHomeAddressAsOwner,
    isMailingAddressAsOwner,
    ssnLast4
  },
  zipCodeValidationStatus
) => {
  const result = {}

  const SSN_REGEX = /^\d{9}$/
  const EMAIL_REGEX =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/

  const isDependent = relation === relationship.dependent
  const isOwner = relation === relationship.owner

  const dobValidation = validateDOB(ageRequirments, dob, relation)
  if (!dobValidation.isValid) {
    result.dob = dobValidation.error
  }

  if ((!email || !email?.toLowerCase()?.match(EMAIL_REGEX)) && !isDependent) {
    result.email = $t('errors.validation.invalidEmail')
  }
  if (!sex) {
    result.sex = $t('errors.validation.missing')
  }
  if (!firstName) {
    result.firstName = $t('errors.validation.invalidFirstName')
  }
  if (!lastName) {
    result.lastName = $t('errors.validation.invalidLastName')
  }
  if (!phone && !isDependent) {
    result.phone = $t('errors.validation.invalidPhone')
  } else if (!!phone && !isValidPhoneNumber(phone)) {
    result.phone = $t('errors.validation.invalidPhone')
  }
  if (!prefferedLanguage && !isDependent) {
    result.prefferedLanguage = $t('errors.validation.missing')
  }
  if (!ssnLast4 && (!ssn || !ssn?.match(SSN_REGEX))) {
    result.ssn = $t('errors.validation.invalidSsn')
  }

  // if home address is same as owner
  // and relation is not owner
  // no need to validate home address
  if (!isHomeAddressAsOwner) {
    if (!homeAddress?.zipCode) {
      result.homeAddress = { ...result.homeAddress }
      result.homeAddress.zipCode = $t('errors.validation.invalidZipcodeNumber')
    } else if (!validateZipcode(homeAddress?.zipCode)) {
      result.homeAddress = { ...result.homeAddress }
      result.homeAddress.zipCode = $t('errors.validation.invalidZipcodeNumber')
    }
    if (
      !homeAddress?.county &&
      zipCodeValidationStatus !== ZIP_CODE_NOT_FOUND
    ) {
      result.homeAddress = { ...result.homeAddress }
      result.homeAddress.county = $t('errors.validation.missingCounty')
    }
    if (!homeAddress?.city) {
      result.homeAddress = { ...result.homeAddress }
      result.homeAddress.city = $t('errors.validation.missingCity')
    }
    if (!homeAddress?.address1) {
      result.homeAddress = { ...result.homeAddress }
      result.homeAddress.address1 = $t('errors.validation.missingStreet')
    }
  }

  // if mailing address is same as owner
  // and relation is not owner
  // no need to validate mailing address
  // if mailing address is same as home address
  // and relation is owner
  // no need to validate mailing address
  if (
    (!isMailingAddressAsHomeAddress && isOwner) ||
    (!isMailingAddressAsOwner && !isOwner)
  ) {
    if (!mailingAddress?.zipCode) {
      result.mailingAddress = { ...result.mailingAddress }
      result.mailingAddress.zipCode = $t(
        'errors.validation.invalidZipcodeNumber'
      )
    } else if (!validateZipcode(mailingAddress?.zipCode)) {
      result.mailingAddress = { ...result.mailingAddress }
      result.mailingAddress.zipCode = $t(
        'errors.validation.invalidZipcodeNumber'
      )
    }
    if (!mailingAddress?.address1) {
      result.mailingAddress = { ...result.mailingAddress }
      result.mailingAddress.address1 = $t('errors.validation.missingStreet')
    }
    if (!mailingAddress?.city) {
      result.mailingAddress = { ...result.mailingAddress }
      result.mailingAddress.city = $t('errors.validation.missingCity')
    }
  }

  return (!isEmpty(result) && result) || null
}

const Details = () => {
  const dispatch = useDispatch()
  const location = useLocation()
  const lastHash = useRef('')
  const ageRequirments = useSelector(
    state => state.app.appConfig?.age_limitations
  )
  const { members, zipCodeValidationStatus, memberQualifyLifeEvent } =
    useSelector(state => state?.member)

  // we cannot sort directly on the store array
  // because sort is modifing the array in place
  // this will resort on every render
  const sortedMembers = [...members].sort(sortByRelation)

  const selectedPlan = useSelector(state => state?.plan?.selectedPlan)

  const [shouldValidate, setShouldValidate] = useState(false)
  const handleInvalid = () => {
    setShouldValidate(true)
    scrollToError()
  }

  const validationFunction = ({ members, ageRequirments }) => {
    const errors = members?.map(member =>
      getValidationErrors(ageRequirments, { ...member })
    )
    return errors?.some(error => error)
  }

  // This hook will validate the application before saving
  useApplicationValidation({
    validationFunction: validationFunction,
    validationParams: { ageRequirments, members },
    handleInvalid,
    handleValid: null
  })

  const memberErrors =
    shouldValidate &&
    sortedMembers?.map(member =>
      getValidationErrors(ageRequirments, member, zipCodeValidationStatus)
    )

  useEffect(() => {
    if (location.hash) {
      // we need to remove the query params from the hash
      const hash = location.hash.split('?')?.[0]
      lastHash.current = hash?.slice(1) // safe hash for further use after navigation (remove #)
    }

    if (lastHash.current && document.getElementById(lastHash.current)) {
      setTimeout(() => {
        document
          .getElementById(lastHash.current)
          ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
        lastHash.current = ''
      }, 100)
    }
  }, [location])

  const onMemberChange = member => {
    dispatch(Actions.createAction(Actions.MEMBER_CHANGED, member))
  }

  const onSubmit = () => {
    const memberErrors = sortedMembers?.map(member =>
      getValidationErrors(ageRequirments, member, zipCodeValidationStatus)
    )

    if (!memberErrors?.find(errors => !isEmpty(errors))) {
      setShouldValidate(false)
      if (zipCodeValidationStatus !== ZIP_CODE_NOT_FOUND) {
        dispatch(
          Actions.createAction(Actions.STEP_COMPLETED, { step: STEPS.DETAILS })
        )
      } else {
        dispatch(
          Actions.createAction(Actions.NAVIGATED_TO, routes.STATE_NOT_ALLOWED)
        )
      }
      return
    }

    setShouldValidate(true)
    scrollToError()
  }

  return (
    <Page
      className='enroll-member-details'
      title={$t('enroll.details.title')}
      backImage={'back_image_enroll.svg'}
      canSkip={true}
    >
      <SelectedPlanInfo
        planName={selectedPlan?.translated_name}
        monthlyPremium={'$' + selectedPlan?.plan_rate_multiplied}
        effectiveDate={memberQualifyLifeEvent?.coverageStartDate}
      />
      {sortedMembers?.map((member, index) => (
        <MemberDetailsCard
          memberErrors={memberErrors?.[index]}
          onChange={member => onMemberChange(member)}
          member={member}
          key={member?.id}
        />
      ))}
      <Button type='primary' onClick={onSubmit} className='continue-btn'>
        {$t('common.continue')}
      </Button>
    </Page>
  )
}

export default Details
